// src/pages/Groups/GroupsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getGroupsList,
  deleteGroup,
  activateGroup,
  updateOrderGroup,
} from "../../services/groupServices";
import Button from "../../components/Button/Button";
import ButtonEdit from "../../components/Button/ButtonEdit";
import ButtonDelete from "../../components/Button/ButtonDelete";
import { FaPlus } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreateGroup from "./CreateGroup";
import EditGroupModal from "./EditGroupModal";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";

const STATUS_FILTERS = [
  { value: "all", label: "Tous les groupes" },
  { value: "active", label: "Actifs uniquement" },
  { value: "inactive", label: "Inactifs uniquement" },
];

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const applyFiltersAndSort = (groups, term, status) => {
  let list = Array.isArray(groups) ? [...groups] : [];
  const q = normalize(term);

  if (q) {
    list = list.filter((g) => normalize(g.nom).includes(q));
  }

  if (status === "active") {
    list = list.filter((g) => g.is_active);
  } else if (status === "inactive") {
    list = list.filter((g) => !g.is_active);
  }

  // Tri par ordre_groupe (fallback 0)
  list.sort(
    (a, b) =>
      (a.ordre_groupe ?? 0) - (b.ordre_groupe ?? 0)
  );

  return list;
};

const GroupsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  // const userRole = user ? user.role : "guest"; // si tu en as besoin plus tard
  const [allGroups, setAllGroups] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isOrdering, setIsOrdering] = useState(false);

  const fetchGroups = async () => {
    try {
      const data = await getGroupsList();
      setAllGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes", error);
      toast.error("Erreur lors de la récupération des groupes");
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const filteredGroups = useMemo(
    () => applyFiltersAndSort(allGroups, searchTerm, statusFilter),
    [allGroups, searchTerm, statusFilter]
  );

  const totalGroups = allGroups.length;
  const activeCount = allGroups.filter((g) => g.is_active).length;
  const inactiveCount = totalGroups - activeCount;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleDeleteGroup = async (group) => {
    if (group.nombreActeurs > 0) {
      toast.error("Impossible de supprimer un groupe avec des membres actifs.");
      return;
    }

    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le groupe "${group.nom}" ?`
      )
    ) {
      return;
    }

    try {
      await deleteGroup(group.id);
      toast.success("Groupe supprimé avec succès");
      setAllGroups((prev) => prev.filter((g) => g.id !== group.id));
    } catch (error) {
      toast.error("Erreur lors de la suppression d’un groupe");
      console.error("Erreur lors de la suppression d’un groupe", error);
    }
  };

  const toggleActiveStatus = async (groupId) => {
    const previous = allGroups;
    const updated = allGroups.map((g) =>
      g.id === groupId ? { ...g, is_active: !g.is_active } : g
    );

    setAllGroups(updated);
    try {
      await activateGroup(groupId);
      toast.success("Statut du groupe mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut du groupe");
      console.error("Erreur lors de la mise à jour du statut du groupe", error);
      setAllGroups(previous); // rollback
    }
  };

  const openEditModal = (group) => {
    setSelectedGroup(group);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedGroup(null);
    setIsEditModalOpen(false);
  };

  /** Réordonner (up/down) en se basant sur la liste filtrée actuelle */
  const moveGroup = async (groupId, direction) => {
    if (isOrdering) return;
    setIsOrdering(true);

    const prevList = allGroups;
    try {
      const currentFiltered = applyFiltersAndSort(
        allGroups,
        searchTerm,
        statusFilter
      );
      const idx = currentFiltered.findIndex((g) => g.id === groupId);
      if (idx === -1) {
        setIsOrdering(false);
        return;
      }

      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= currentFiltered.length) {
        setIsOrdering(false);
        return;
      }

      const currentId = currentFiltered[idx].id;
      const targetId = currentFiltered[targetIdx].id;

      const list = [...allGroups];
      const i = list.findIndex((g) => g.id === currentId);
      const j = list.findIndex((g) => g.id === targetId);
      if (i === -1 || j === -1) {
        setIsOrdering(false);
        return;
      }

      // Swap des ordre_groupe
      const tmp = list[i].ordre_groupe ?? 0;
      list[i].ordre_groupe = list[j].ordre_groupe ?? tmp;
      list[j].ordre_groupe = tmp;

      // Tri par ordre
      list.sort(
        (a, b) => (a.ordre_groupe ?? 0) - (b.ordre_groupe ?? 0)
      );

      setAllGroups(list);

      // Persister la nouvelle ordre
      await updateOrderGroup(list);
      toast.success("Ordre des groupes mis à jour");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'ordre des groupes", error);
      toast.error("Erreur lors de la mise à jour de l'ordre des groupes");
      setAllGroups(prevList); // rollback
    } finally {
      setIsOrdering(false);
    }
  };

  const moveGroupUp = (groupId) => moveGroup(groupId, "up");
  const moveGroupDown = (groupId) => moveGroup(groupId, "down");

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* TITRE */}
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-2 uppercase">
        ADMINISTRATEURS
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-6 font-montserrat uppercase">
        Groupes du Judo
      </h2>

      {/* BANDEAU FILTRES + STATS */}
      <div className="w-full max-w-[1280px] mx-auto mb-5 rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/70 backdrop-blur">
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
          <div className="px-4 sm:px-6 lg:px-8 py-5 text-white">
            <div className="flex flex-col gap-4 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] opacity-80">
                  STRUCTURATION DES ACTEURS
                </p>
                <p className="mt-1 text-lg sm:text-xl font-semibold">
                  Gérer les groupes, leur ordre d’affichage et leur statut.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-start sm:justify-end text-[11px] sm:text-xs">
                <div className="rounded-xl bg-white/90 text-slate-900 px-3 py-1.5 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                  <span className="font-semibold">Total</span>
                  <span className="opacity-70">({totalGroups})</span>
                </div>
                <div className="rounded-xl bg-white/90 text-slate-900 px-3 py-1.5 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span className="font-semibold">Actifs</span>
                  <span className="opacity-70">({activeCount})</span>
                </div>
                <div className="rounded-xl bg-white/90 text-slate-900 px-3 py-1.5 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                  <span className="font-semibold">Inactifs</span>
                  <span className="opacity-70">({inactiveCount})</span>
                </div>
              </div>
            </div>

            {/* Filtres */}
            <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <div className="flex-1">
                <input
                  className="input input-bordered w-full text-sm text-slate-900 placeholder:text-slate-400 bg-white/95 border-white/60 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  type="text"
                  placeholder="Rechercher un groupe par nom..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="select select-bordered text-xs sm:text-sm bg-white/95 border-white/60 text-slate-900 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                >
                  {STATUS_FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <Button
                  text="Ajouter un groupe"
                  onClick={() => setIsCreateModalOpen(true)}
                  icon={FaPlus}
                  className="!rounded-xl !px-4 !py-2 gap-2 bg-white/95 text-slate-900 hover:bg-slate-100"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODALES */}
      <CreateGroup
        isActive={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        refreshGroups={fetchGroups}
      />
      {isEditModalOpen && (
        <EditGroupModal
          isActive={isEditModalOpen}
          onClose={closeEditModal}
          refreshGroups={fetchGroups}
          groupData={selectedGroup}
        />
      )}

      {/* TABLEAU GROUPES */}
      <div className="overflow-x-auto shadow-xl rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-900 max-w-[1280px] mx-auto mb-8">
        {filteredGroups.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-600 dark:text-slate-300">
            Aucun groupe ne correspond à vos filtres.
          </div>
        ) : (
          <table className="table bg-transparent font-montserrat border-collapse w-full">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Nom du groupe
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Image
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Ordre
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Nb d'acteurs
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((group, index) => (
                <tr
                  key={group.id}
                  className="text-left border-t border-slate-100 dark:border-slate-700"
                >
                  {/* Nom + couleur */}
                  <td className="px-5 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex h-8 min-w-[2.5rem] items-center justify-center rounded-full text-xs font-semibold ${
                          group.color_groupe || "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {group.nom?.charAt(0) || "?"}
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {group.nom}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-300">
                          ID #{group.id} • ordre {group.ordre_groupe}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Image */}
                  <td className="px-5 py-3 align-top">
                    <div className="flex flex-col items-start">
                      <img
                        src={group.photo_url || "./img/default_image.jpg"}
                        alt={group.nom}
                        className="w-28 h-16 object-cover rounded-md ring-1 ring-slate-200 dark:ring-slate-700"
                      />
                      <button
                        type="button"
                        onClick={() => openEditModal(group)}
                        className="mt-2 text-[11px] text-primary hover:text-primary/80 underline"
                      >
                        Modifier l'image / le groupe
                      </button>
                    </div>
                  </td>

                  {/* Ordre + flèches */}
                  <td className="px-5 py-3 align-top">
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs text-slate-800 dark:text-slate-100">
                      <span className="font-semibold">
                        {group.ordre_groupe}
                      </span>
                      <div className="flex flex-col ml-1">
                        <button
                          type="button"
                          onClick={() => moveGroupUp(group.id)}
                          className={`text-xs ${
                            index === 0
                              ? "text-slate-300 cursor-not-allowed"
                              : "text-blue-500 hover:text-blue-700"
                          }`}
                          disabled={index === 0 || isOrdering}
                          title="Monter"
                        >
                          <TbTriangleFilled />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveGroupDown(group.id)}
                          className={`text-xs ${
                            index === filteredGroups.length - 1
                              ? "text-slate-300 cursor-not-allowed"
                              : "text-blue-500 hover:text-blue-700"
                          }`}
                          disabled={index === filteredGroups.length - 1 || isOrdering}
                          title="Descendre"
                        >
                          <TbTriangleInvertedFilled />
                        </button>
                      </div>
                    </div>
                  </td>

                  {/* Statut actif */}
                  <td className="px-5 py-3 align-top">
                    <button
                      type="button"
                      onClick={() => toggleActiveStatus(group.id)}
                      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold border ${
                        group.is_active
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-rose-50 text-rose-700 border-rose-200"
                      }`}
                    >
                      <span
                        className={`mr-2 h-2 w-2 rounded-full ${
                          group.is_active ? "bg-emerald-500" : "bg-rose-500"
                        }`}
                      />
                      {group.is_active ? "Actif" : "Inactif"}
                    </button>
                  </td>

                  {/* Nombre acteurs */}
                  <td className="px-5 py-3 align-top">
                    <span className="inline-flex min-w-[2.5rem] justify-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                      {group.nombreActeurs || 0}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <ButtonEdit
                        onClick={() => openEditModal(group)}
                        hoverLabel="Éditer"
                      />
                      <ButtonDelete
                        onClick={() => handleDeleteGroup(group)}
                        hoverLabel="Supprimer"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default GroupsPage;
